import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { fetchPropensityHierarchyRequested } from '@/store/slices/propensityHierarchySlice';
import { fetchPropensityHierarchyBreakdown, PropensityHierarchyNode } from '@/services/api';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  Stack,
  Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';

export default function PropensityManagerDrillDown() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { node, isLoading, error } = useSelector((s: RootState) => s.propensityHierarchy);
  const [stackPath, setStackPath] = useState<{ id?: string; role?: string; path?: string }[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [children, setChildren] = useState<Record<string, PropensityHierarchyNode | null>>({});
  const [childLoading, setChildLoading] = useState<Record<string, boolean>>({});
  const [childError, setChildError] = useState<Record<string, string | null>>({});

  useEffect(() => {
    if (!node) {
      dispatch(fetchPropensityHierarchyRequested({}));
    }
  }, [dispatch, node]);

  const drillInto = useCallback((employee_id?: string, role?: string, hierarchy_path?: string) => {
    setStackPath(prev => [...prev, { id: employee_id, role, path: hierarchy_path }]);
    dispatch(fetchPropensityHierarchyRequested({ target_employee_id: employee_id, target_role: role, hierarchy_path }));
  }, [dispatch]);

  const toggleExpand = useCallback(async (empId: string, role?: string) => {
    setExpanded(prev => ({ ...prev, [empId]: !prev[empId] }));
    const opening = !expanded[empId];
    if (opening && !children[empId]) {
      try {
        setChildLoading(prev => ({ ...prev, [empId]: true }));
        setChildError(prev => ({ ...prev, [empId]: null }));
        const data = await fetchPropensityHierarchyBreakdown({ target_employee_id: empId, target_role: role });
        setChildren(prev => ({ ...prev, [empId]: data }));
      } catch (e: any) {
        setChildError(prev => ({ ...prev, [empId]: e?.message || 'Failed to load details' }));
      } finally {
        setChildLoading(prev => ({ ...prev, [empId]: false }));
      }
    }
  }, [expanded, children]);

  const goBack = useCallback(() => {
    setStackPath(prev => {
      const next = [...prev];
      next.pop();
      const last = next[next.length - 1];
      dispatch(fetchPropensityHierarchyRequested({
        target_employee_id: last?.id,
        target_role: last?.role,
        hierarchy_path: last?.path
      }));
      return next;
    });
  }, [dispatch]);

  const renderPoliciesTable = useCallback((policies: NonNullable<PropensityHierarchyNode['policies']>) => (
    <Table size="small" sx={{ 
      mt: 2,
      tableLayout: 'fixed',
      '& .MuiTableCell-root': {
        px: 1,
        py: 0.75,
        borderColor: theme.palette.divider,
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderTop: `1px solid ${theme.palette.divider}`,
      },
      '& .MuiTableRow-root:nth-of-type(odd)': {
        backgroundColor: 'rgba(0, 0, 0, 0.01)',
      },
      '& .MuiTableCell-root:first-of-type': { borderLeft: 'none' },
      '& .MuiTableCell-root:last-of-type': { borderRight: 'none' }
    }}>
      <colgroup>
        <col style={{ width: '22%' }} />
        <col style={{ width: '18%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '8%' }} />
      </colgroup>
      <TableHead>
        <TableRow>
          <TableCell>Policy</TableCell>
          <TableCell>Agent</TableCell>
          <TableCell>Channel</TableCell>
          <TableCell>Sub Channel</TableCell>
          <TableCell>Premium (₹)</TableCell>
          <TableCell>Renewal Probability</TableCell>
          <TableCell>Prediction</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {policies.map((p) => {
          const prob = Number(p.renewal_probability || 0);
          const isRenew = p.predicted_renewal === 1;
          const formattedPremium = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(p.premium_amount || 0));
          return (
            <TableRow key={p.policy_id} hover>
              <TableCell>{p.policy_id}</TableCell>
              <TableCell>{p.agent_code}</TableCell>
              <TableCell>{p.channel}</TableCell>
              <TableCell>{p.sub_channel}</TableCell>
              <TableCell>{formattedPremium}</TableCell>
              <TableCell>{`${Math.round(Math.max(0, Math.min(1, prob)) * 100)}%`}</TableCell>
              <TableCell>
                <Chip size="small" color={isRenew ? 'success' : 'warning'} label={isRenew ? 'Likely Renewal' : 'At Risk'} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  ), []);

  const renderSubordinates = useCallback((current: PropensityHierarchyNode, level: number = 0) => (
    <Table size="small" sx={{ 
      tableLayout: 'fixed',
      width: '100%',
      '& .MuiTableCell-root': {
        px: 1,
        py: 0.75,
        borderColor: theme.palette.divider,
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderTop: `1px solid ${theme.palette.divider}`,
      },
      '& .MuiTableRow-root:nth-of-type(odd)': {
        backgroundColor: 'rgba(0, 0, 0, 0.01)',
      },
      '& .MuiTableCell-root:first-of-type': { borderLeft: 'none' },
      '& .MuiTableCell-root:last-of-type': { borderRight: 'none' }
    }}>
      <colgroup>
        <col style={{ width: '30%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '14%' }} />
      </colgroup>
      {level === 0 && (
        <TableHead>
          <TableRow>
            <TableCell>Employee</TableCell>
            <TableCell>Role</TableCell>
            <TableCell align="right">Policies</TableCell>
            <TableCell align="right">Predicted Renewals</TableCell>
            <TableCell align="right">Avg Prob</TableCell>
            <TableCell align="right">Renewal Rate</TableCell>
            <TableCell align="right">Risk Split</TableCell>
          </TableRow>
        </TableHead>
      )}
      <TableBody>
        {(current.subordinates || []).map((sub) => {
          const key = sub.employee_id;
          const isOpen = !!expanded[key];
          const role = (sub as any).role || sub.employee_role?.toLowerCase();
          const child = children[key];
          return (
            <React.Fragment key={key}>
              <TableRow hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => {
                        const wasOpen = !!expanded[key];
                        const willOpen = !wasOpen;
                        setExpanded(prev => ({ ...prev, [key]: willOpen }));
                        if (willOpen && !children[key]) {
                          (async () => {
                            try {
                              setChildLoading(prev => ({ ...prev, [key]: true }));
                              setChildError(prev => ({ ...prev, [key]: null }));
                              const data = await fetchPropensityHierarchyBreakdown({ target_employee_id: key, target_role: role });
                              setChildren(prev => ({ ...prev, [key]: data }));
                            } catch (e: any) {
                              setChildError(prev => ({ ...prev, [key]: e?.message || 'Failed to load details' }));
                            } finally {
                              setChildLoading(prev => ({ ...prev, [key]: false }));
                            }
                          })();
                        }
                      }}
                      aria-label={isOpen ? 'collapse' : 'expand'}
                      sx={{ transition: 'transform 0.3s ease', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', color: theme.palette.primary.main, p: 0.5 }}
                    >
                      <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="body2" fontWeight={700}>{sub.employee_name}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>{sub.employee_role}</TableCell>
                <TableCell align="right">{sub.total_policies}</TableCell>
                <TableCell align="right">{sub.total_predicted_renewals}</TableCell>
                <TableCell align="right">{(Number(current.average_renewal_probability) * 100).toFixed(1)}%</TableCell>
                <TableCell align="right">{sub.predicted_renewal_rate}%</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Chip size="small" color="error" label={`H:${sub.high_risk_count}`} />
                    <Chip size="small" color="warning" label={`M:${sub.medium_risk_count}`} />
                    <Chip size="small" color="success" label={`L:${sub.low_risk_count}`} />
                  </Stack>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={7} sx={{ p: 0, border: 'none', bgcolor: `${theme.palette.primary.main}08`, position: 'relative', '&::before': { content: '""', position: 'absolute', left: 28, top: 0, bottom: 0, width: 2, bgcolor: `${theme.palette.primary.main}40` } }}>
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box sx={{ p: 2 }}>
                      {childLoading[key] && <LinearProgress />}
                      {childError[key] && <Alert severity="error">{childError[key]}</Alert>}
                      {child && (
                        <>
                          {(child.subordinates?.length || 0) > 0 && (
                            renderSubordinates(child, level + 1)
                          )}
                          {(child.policies?.length || 0) > 0 && (
                            renderPoliciesTable(child.policies!)
                          )}
                        </>
                      )}
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  ), [expanded, children, childLoading, childError, renderPoliciesTable, theme]);

  if (isLoading) return (
    <Box sx={{ my: 1 }}><LinearProgress /></Box>
  );
  if (error) return (
    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
  );
  if (!node) return null;

  const hasSubordinates = (node.subordinates?.length || 0) > 0;
  const hasPolicies = (node.policies?.length || 0) > 0;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          {node.employee_name} ({node.employee_role})
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip size="small" label={`Policies: ${node.total_policies}`} />
          <Chip size="small" color="success" label={`Predicted Renewals: ${node.total_predicted_renewals}`} />
          <Chip size="small" color="primary" label={`Avg Prob: ${(Number(node.average_renewal_probability) * 100).toFixed(1)}%`} />
          <Chip size="small" color="warning" label={`Renewal Rate: ${node.predicted_renewal_rate}%`} />
        </Stack>
      </Stack>

      {stackPath.length > 0 && (
        <Button variant="outlined" size="small" onClick={goBack} sx={{ mb: 2 }}>Back</Button>
      )}

      {hasSubordinates && (
        <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 2 }}>
          {renderSubordinates(node)}
        </TableContainer>
      )}

      {hasPolicies && (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          {renderPoliciesTable(node.policies!)}
        </TableContainer>
      )}
    </Box>
  );
}


